// @ts-nocheck
// Type checking disabled for this file due to Firebase Firestore SDK type complexity
// ============================================
// Firebase Project Service
// Сервис операций с проектами Firebase
// ============================================

import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  getDoc,
  getDocs,
  query,
  collection,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where,
  limit as firestoreLimit,
  getFirestore,
  increment,
  writeBatch
} from 'firebase/firestore';
import { httpsCallable, Functions } from 'firebase/functions';
import { isFirebaseConfigured, getFirebaseApp, getFirebaseFunctions } from '../firebase';
import { validate, safeValidate, type ValidationResult } from '../validators';
import type { User } from '../../types/auth';
import type { 
  App, 
  AppVersion, 
  Screen, 
  HistoryRecord, 
  HistoryAction, 
  HistoryEntityType, 
  AnalyticsEvent,
  EventType,
  EventStatus,
  EventParam,
  Comment 
} from '../../types';
import { PLANS, getPlanLimits, DEMO_LIMITS, type PlanType } from '../plans';
import { getVersionLimits, getScreenLimits, getEventLimits } from '../limits';
import { handleFirebaseError, showError } from '../errorHandler';

// Lazy getter для db - избегаем race condition
const getDbInstance = () => {
  try {
    const app = getFirebaseApp();
    return getFirestore(app);
  } catch {
    return null;
  }
};

// Lazy getter для functions
const getFunctionsInstance = (): Functions | null => {
  try {
    return getFirebaseFunctions();
  } catch {
    return null;
  }
};

// Генерирует уникальный ID
const generateId = () => Math.random().toString(36).substring(2, 15) + Date.now().toString(36);

// ============================================
// Управление счётчиками лимитов пользователя
// ============================================

// Константы лимитов - импортируются из планов (по умолчанию Free)
export const LIMITS = {
  MAX_PROJECTS: PLANS.free.limits.maxProjects,
  MAX_DEMO_PROJECTS: DEMO_LIMITS.MAX_DEMO_PROJECTS,
  MAX_VERSIONS: PLANS.free.limits.maxVersions,
  MAX_SCREENS: PLANS.free.limits.maxScreens,
  MAX_EVENTS: PLANS.free.limits.maxEvents
};

export interface UserLimits {
  projectCount: number;
  demoProjectCount: number;
  maxProjects: number;
  maxDemoProjects: number;
  maxVersions?: number;
  maxScreens?: number;
  maxEvents?: number;
  maxMembers?: number;
}

// Обновление счётчика проектов при создании/удалении
const updateProjectCount = async (userId: string, isDemo: boolean, delta: number): Promise<void> => {
  if (!userId) return;
  
  const db = getDbInstance();
  if (!db) return;
  
  try {
    const field = isDemo ? 'demoProjectCount' : 'projectCount';
    await updateDoc(doc(db, 'users', userId), {
      [field]: increment(delta)
    });
    console.log(`[updateProjectCount] Updated ${field} for user ${userId}: ${delta > 0 ? '+' : ''}${delta}`);
  } catch (error) {
    console.error('[updateProjectCount] Error:', error);
  }
};

// Получение данных о лимитах пользователя
export const getUserLimits = async (userId: string): Promise<{
  projectCount: number;
  demoProjectCount: number;
  maxProjects: number;
  maxDemoProjects: number;
  maxVersions: number;
  maxScreens: number;
  maxEvents: number;
  maxMembers: number;
  plan: PlanType;
}> => {
  if (!userId) {
    return {
      projectCount: 0,
      demoProjectCount: 0,
      maxProjects: PLANS.free.limits.maxProjects,
      maxDemoProjects: DEMO_LIMITS.MAX_DEMO_PROJECTS,
      maxVersions: PLANS.free.limits.maxVersions,
      maxScreens: PLANS.free.limits.maxScreens,
      maxEvents: PLANS.free.limits.maxEvents,
      maxMembers: PLANS.free.limits.maxMembers,
      plan: 'free'
    };
  }
  
  const db = getDbInstance();
  if (!db) {
    return {
      projectCount: 0,
      demoProjectCount: 0,
      maxProjects: PLANS.free.limits.maxProjects,
      maxDemoProjects: DEMO_LIMITS.MAX_DEMO_PROJECTS,
      maxVersions: PLANS.free.limits.maxVersions,
      maxScreens: PLANS.free.limits.maxScreens,
      maxEvents: PLANS.free.limits.maxEvents,
      maxMembers: PLANS.free.limits.maxMembers,
      plan: 'free'
    };
  }
  
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      // Получаем план пользователя (по умолчанию 'free')
      const planType = (data.plan as PlanType) || 'free';
      const planLimits = getPlanLimits(planType);
      
      return {
        projectCount: data.projectCount || 0,
        demoProjectCount: data.demoProjectCount || 0,
        maxProjects: planLimits.maxProjects,
        maxDemoProjects: planLimits.maxDemoProjects,
        maxVersions: planLimits.maxVersions,
        maxScreens: planLimits.maxScreens,
        maxEvents: planLimits.maxEvents,
        maxMembers: planLimits.maxMembers,
        plan: planType
      };
    }
    return { 
      projectCount: 0, 
      demoProjectCount: 0, 
      maxProjects: PLANS.free.limits.maxProjects, 
      maxDemoProjects: DEMO_LIMITS.MAX_DEMO_PROJECTS,
      maxVersions: PLANS.free.limits.maxVersions,
      maxScreens: PLANS.free.limits.maxScreens,
      maxEvents: PLANS.free.limits.maxEvents,
      plan: 'free'
    };
  } catch (error) {
    console.error('[getUserLimits] Error:', error);
    return {
      projectCount: 0,
      demoProjectCount: 0,
      maxProjects: PLANS.free.limits.maxProjects,
      maxDemoProjects: DEMO_LIMITS.MAX_DEMO_PROJECTS,
      maxVersions: PLANS.free.limits.maxVersions,
      maxScreens: PLANS.free.limits.maxScreens,
      maxEvents: PLANS.free.limits.maxEvents,
      maxMembers: PLANS.free.limits.maxMembers,
      plan: 'free'
    };
  }
};



// Проверяет доступность Firebase
const isFirebaseAvailable = (): boolean => {
  try {
    const hasWindow = typeof window !== 'undefined';
    const isConfigured = isFirebaseConfigured();
    const db = getDbInstance();
    
    // DEBUG: Detailed logging
    console.log('[isFirebaseAvailable projectService] Debug:', {
      hasWindow,
      isConfigured,
      hasDb: !!db
    });
    
    return hasWindow && isConfigured && !!db;
  } catch (error) {
    console.error('[isFirebaseAvailable projectService] Error:', error);
    return false;
  }
};

// Логирование истории
const logHistory = async (
  action: HistoryAction,
  entityType: HistoryEntityType,
  entityId: string,
  projectId: string,
  oldValue: unknown,
  newValue: unknown,
  user: User
): Promise<void> => {
  if (!isFirebaseAvailable()) {
    console.warn('[logHistory] Firebase not available, skipping');
    return;
  }

  const db = getDbInstance();
  if (!db) {
    console.warn('[logHistory] DB instance not available, skipping');
    return;
  }

  // DEBUG: Log attempt to write history
  console.log('[logHistory] Attempting to write history:', {
    action,
    entityType,
    entityId,
    projectId,
    userId: user.id,
    userRole: user.role,
    userEmail: user.email
  });

  const actionText = action === 'create' ? 'створив' : action === 'update' ? 'оновив' : 'видалив';
  const entityText = entityType === 'project' ? 'проект' 
    : entityType === 'version' ? 'версію' 
    : entityType === 'screen' ? 'екран' 
    : entityType === 'event' ? 'подію' 
    : 'коментар';
  
  const oldObj = oldValue as Record<string, unknown> | null | undefined;
  const newObj = newValue as Record<string, unknown> | null | undefined;
  const name = (newObj?.name || newObj?.displayName || oldObj?.name || oldObj?.displayName || entityId.substring(0, 8)) as string;

  // Получаем данные проекта для сбора всех участников
  let allowedUsers: string[] = [user.id]; // По умолчанию только текущий пользователь
  let projectOwnerId: string = user.id; // ID владельца проекта для записи в историю
  
  try {
    const projectSnap = await getDoc(doc(db, 'apps', projectId));
    if (projectSnap.exists()) {
      const projectData = projectSnap.data();
      const ownerId = projectData.ownerId;
      const membersIds = Object.keys(projectData.members || {});
      
      // Сохраняем реальный ID владельца проекта
      projectOwnerId = ownerId;
      
      // Собираем всех участников проекта (владелец + все members)
      // Используем Set для удаления дубликатов
      allowedUsers = Array.from(new Set([ownerId, ...membersIds]));
      
      console.log('[logHistory] Collected project members:', { ownerId, membersIds, allowedUsers });
    } else {
      console.warn('[logHistory] Project not found, using only current user');
    }
  } catch (error) {
    console.warn('[logHistory] Error fetching project members, using only current user:', error);
  }
  
  const historyRecord = {
    projectId,
    action,
    entityType,
    entityId,
    entityName: name,
    userId: user.id,
    userName: user.displayName || user.email || 'Unknown User',
    userRole: user.role,
    // Денормализация для оптимизации Firestore rules
    // Храним ownerId и members чтобы не делать get() при каждом чтении истории
    ownerId: projectOwnerId, // ID владельца проекта для фильтрации истории
    allowedUsers, // массив всех участников проекта для фильтрации array-contains
    oldValue: oldValue !== null && oldValue !== undefined ? JSON.stringify(oldValue) : null,
    newValue: newValue !== null && newValue !== undefined ? JSON.stringify(newValue) : null,
    timestamp: new Date(),
    description: `${user.displayName} (${user.role}) ${actionText} ${entityText}: ${name}`
  };

  try {
    await setDoc(doc(collection(db, 'history')), {
      ...historyRecord,
      timestamp: serverTimestamp()
    });
    console.log('[logHistory] Successfully wrote history record');
  } catch (error: any) {
    console.error('[logHistory] Error logging history:', error?.code, error?.message);
  }
};

// Создание проекта через Cloud Function
// Использует httpsCallable для обхода Firestore Rules
export const createProject = async (data: { name: string; description: string }, user: User): Promise<App | null> => {
  console.log('[createProject] Starting with user:', { id: user.id, role: user.role, displayName: user.displayName });
  
  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[createProject] Functions not available');
    return null;
  }

  try {
    // Используем httpsCallable для создания проекта
    const createProjectFn = httpsCallable(functions, 'createProject');
    const result = await createProjectFn({
      name: data.name,
      description: data.description,
      isDemo: false
    });
    
    const response = result.data as { appId: string; success: boolean };
    
    if (response.success && response.appId) {
      console.log('[createProject] Project created via Cloud Function:', response.appId);
      
      // Получаем созданный проект из Firestore чтобы получить все данные включая версии
      const createdApp = await getProject(response.appId);
      
      if (createdApp) {
        console.log('[createProject] Fetched created project with versions:', createdApp.versions?.length || 0);
        await logHistory('create', 'project', response.appId, response.appId, null, { name: data.name, description: data.description }, user);
        return createdApp;
      }
      
      // Fallback: если не удалось получить проект, создаём локально
      console.warn('[createProject] Could not fetch created project, using fallback');
      const newApp: App = {
        id: response.appId,
        name: data.name,
        description: data.description,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        versions: [{ id: 'v_1.0.0', version: '1.0.0', name: 'Версія 1.0', isActive: true, screens: [], eventCount: 0 }],
        ownerId: user.id,
        members: {
          [user.id]: { role: 'analyst', status: 'active', addedAt: new Date().toISOString() }
        }
      };
      
      await logHistory('create', 'project', response.appId, response.appId, null, { name: data.name, description: data.description }, user);
      
      return newApp;
    }
    
    console.error('[createProject] Cloud Function returned error:', result);
    return null;
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[createProject] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Обновление проекта через Cloud Function
export const updateProject = async (id: string, data: Partial<App>, user: User, oldValue?: App): Promise<boolean> => {
  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[updateProject] Functions not available');
    return false;
  }

  // Build data with only provided fields (not undefined)
  // This prevents overwriting existing data with undefined in Firestore
  const updateData: Record<string, any> = {};
  
  if (data.name !== undefined) updateData.name = data.name;
  if (data.description !== undefined) updateData.description = data.description;
  
  console.log('[updateProject] Sending data (only provided fields):', updateData);
  
  try {
    const updateProjectFn = httpsCallable(functions, 'updateProject');
    const result = await updateProjectFn({
      appId: id,
      ...updateData
    });
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      await logHistory('update', 'project', id, id, oldValue, data, user);
      return true;
    }
    
    console.error('[updateProject] Cloud Function returned error:', result);
    return false;
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[updateProject] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Удаление проекта через Cloud Function
export const deleteProject = async (id: string, user: User): Promise<boolean> => {
  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[deleteProject] Functions not available');
    return false;
  }

  try {
    const deleteProjectFn = httpsCallable(functions, 'deleteProject');
    const result = await deleteProjectFn({ appId: id });
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      await logHistory('delete', 'project', id, id, { deleted: true }, null, user);
      return true;
    }
    
    console.error('[deleteProject] Cloud Function returned error:', result);
    return false;
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[deleteProject] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Получение всех проектов пользователя (где он owner или участник)
export const getProjects = async (userId?: string): Promise<App[]> => {
  console.log('[getProjects] ===== STARTING GET PROJECTS =====');
  console.log('[getProjects] userId:', userId);
  console.log('[getProjects] Stack trace:', new Error().stack);
  
  if (!isFirebaseAvailable()) {
    console.log('[getProjects] Firebase not available, returning []');
    return [];
  }

  const db = getDbInstance();
  if (!db) {
    console.log('[getProjects] DB instance not available, returning []');
    return [];
  }

  console.log('[getProjects] DB instance obtained');

  try {
    // Если userId передан, получаем проекты где пользователь owner или участник
    if (userId) {
      console.log('[getProjects] Fetching projects where user is owner or member');
      
      // Делаем два запроса: один для owner, один для участников
      // Это обходит ограничение Firestore на OR запросы
      console.log('[getProjects] 📡 Firestore Query 1: collection=apps, where ownerId==', userId, ', orderBy updatedAt desc');
      
      const [ownerQuerySnapshot, memberQuerySnapshot] = await Promise.all([
        // Запрос 1: проекты где userId - owner
        getDocs(query(
          collection(db, 'apps'),
          where('ownerId', '==', userId),
          orderBy('updatedAt', 'desc')
        )),
        // Запрос 2: проекты где userId есть в members
        console.log('[getProjects] 📡 Firestore Query 2: collection=apps (all for member search), orderBy updatedAt desc'),
        getDocs(query(collection(db, 'apps'), orderBy('updatedAt', 'desc')))
      ]);
      
      const apps: App[] = [];
      const addedIds = new Set<string>();
      
      console.log('[getProjects] Owner query result count:', ownerQuerySnapshot.size);
      console.log('[getProjects] Member query result count:', memberQuerySnapshot.size);
      
      // Добавляем проекты где пользователь owner
      ownerQuerySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('[getProjects] Owner project found:', doc.id, 'name:', data.name, 'ownerId:', data.ownerId);
        apps.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          icon: data.icon,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          versions: data.versions || [],
          ownerId: data.ownerId || '',
          members: data.members || {},
          isDemo: data.isDemo,
          isOnboarding: data.isOnboarding,
          demoForUserId: data.demoForUserId
        });
        addedIds.add(doc.id);
      });
      
      console.log('[getProjects] Added owner projects:', apps.length, 'addedIds:', Array.from(addedIds));
      
      // Добавляем проекты где пользователь участник (но не owner)
      // Включая тех, у кого есть pending приглашение
      memberQuerySnapshot.forEach((doc) => {
        if (addedIds.has(doc.id)) {
          console.log('[getProjects] Skipping (already added):', doc.id);
          return; // Уже добавили
        }
        
        const data = doc.data();
        
        // FIX: Skip if user is owner (check ownerId directly, not addedIds)
        if (data.ownerId === userId) {
          console.log('[getProjects] Skipping (user is owner):', doc.id);
          return;
        }
        
        // Проверяем: либо активный участник (status: 'active' или без статуса для совместимости),
        // либо приглашение в ожидании (status: 'pending'),
        // либо демо-проект с joinedAt (backwards compatibility)
        const member = data.members?.[userId];
        
        // FIX: Improved status check - be more inclusive for backwards compatibility
        const memberStatus = member?.status;
        const isActiveMember = member && (
          memberStatus === 'active' || 
          memberStatus === undefined || 
          memberStatus === null ||
          memberStatus === '' ||
          !memberStatus
        );
        // Firestore rules now allow reading projects with status: 'pending'
        const isPendingInvitation = member && (
          memberStatus === 'pending' ||
          memberStatus === undefined ||
          memberStatus === null
        );
        const isDemoWithJoinedAt = member && member.joinedAt && !memberStatus && data.isDemo;
        
        console.log('[getProjects] Checking member project:', doc.id, 'userId:', userId, 'member:', member, 'isActive:', isActiveMember, 'isPending:', isPendingInvitation, 'isDemoWithJoinedAt:', isDemoWithJoinedAt);
        
        if (isActiveMember || isPendingInvitation || isDemoWithJoinedAt) {
          console.log('[getProjects] Adding member project:', doc.id, 'name:', data.name);
          apps.push({
            id: doc.id,
            name: data.name,
            description: data.description,
            icon: data.icon,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            versions: data.versions || [],
            ownerId: data.ownerId || '',
            members: data.members || {},
            isDemo: data.isDemo,
            isOnboarding: data.isOnboarding,
            demoForUserId: data.demoForUserId
          });
        } else {
          console.log('[getProjects] NOT adding project (criteria not met):', doc.id);
        }
      });
      
      // Сортируем по updatedAt
      apps.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      
      console.log('[getProjects] ===== FINAL RESULT =====');
      console.log('[getProjects] Total projects returned:', apps.length);
      console.log('[getProjects] Projects:', apps.map(a => ({ id: a.id, name: a.name, ownerId: a.ownerId })));
      
      return apps;
    }
    
    // Без userId - возвращаем все проекты (для админских целей)
    console.log('[getProjects] No userId, fetching all projects');
    const q = query(collection(db, 'apps'), orderBy('updatedAt', 'desc'));
    console.log('[getProjects] 📡 Firestore Query (all): collection=apps, orderBy updatedAt desc');
    const querySnapshot = await getDocs(q);
    const apps: App[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('[getProjects] All apps - project:', doc.id, 'name:', data.name, 'ownerId:', data.ownerId);
      apps.push({
        id: doc.id,
        name: data.name,
        description: data.description,
        icon: data.icon,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        versions: data.versions || [],
        ownerId: data.ownerId || '',
        members: data.members || {},
        isDemo: data.isDemo,
        isOnboarding: data.isOnboarding,
        demoForUserId: data.demoForUserId
      });
    });
    
    return apps;
  } catch (error) {
    console.error('Error getting projects:', error);
    return [];
  }
};

// Получение одного проекта
export const getProject = async (id: string): Promise<App | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  try {
    const docSnap = await getDoc(doc(db, 'apps', id));
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name,
        description: data.description,
        icon: data.icon,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        versions: data.versions || [],
        ownerId: data.ownerId || '',
        members: data.members || {},
        isDemo: data.isDemo,
        isOnboarding: data.isOnboarding,
        demoForUserId: data.demoForUserId
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting project:', error);
    return null;
  }
};

// Подписка на проекты пользователя (где он owner или участник)
export const subscribeToProjects = (callback: (apps: App[]) => void, userId?: string) => {
  console.log('[subscribeToProjects] ===== STARTING SUBSCRIPTION =====');
  console.log('[subscribeToProjects] Received callback and userId:', userId);
  console.log('[subscribeToProjects] Stack trace:', new Error().stack);
  
  // CRITICAL FIX: Validate userId before proceeding
  // Empty string, undefined, or null should not trigger Firestore queries
  const validUserId = userId && userId.trim() ? userId.trim() : null;
  
  if (!validUserId) {
    console.warn('[subscribeToProjects] ⚠️ No valid userId provided, skipping subscription. userId:', userId);
    callback([]);
    return () => {};
  }
  
  if (!isFirebaseAvailable()) {
    console.log('[subscribeToProjects] Firebase not available, returning empty array');
    callback([]);
    return () => {};
  }

  const db = getDbInstance();
  if (!db) {
    console.log('[subscribeToProjects] DB instance not available, returning empty array');
    callback([]);
    return () => {};
  }

  console.log('[subscribeToProjects] DB instance obtained, setting up queries for userId:', validUserId);

  // Подписка на проекты где пользователь owner
  const ownerQuery = query(
    collection(db, 'apps'),
    where('ownerId', '==', validUserId),
    orderBy('updatedAt', 'desc')
  );

  console.log('[subscribeToProjects] 📡 Firestore Query 1 (owner): collection=apps, where ownerId==', validUserId, ', orderBy updatedAt desc');

  let ownerUnsub: (() => void) | null = null;
  let invitationsUnsub: (() => void) | null = null;
  let ownerApps: App[] = [];
  let memberApps: App[] = [];
  
  // FIX: Track if both subscriptions are ready before calling callback
  let ownerReady = false;
  let invitationsReady = false;
  
  let callbackScheduled = false;
  
  // Reset callback flag when new data arrives - ensures we call callback after each update
  const resetCallbackFlag = () => {
    callbackScheduled = false;
  };
  
  const checkAndCallCallback = () => {
    // Only schedule callback once when both subscriptions are ready
    if (ownerReady && invitationsReady && !callbackScheduled) {
      callbackScheduled = true;
      
      // Use Set to deduplicate by ID - more efficient than findIndex
      const uniqueAppsMap = new Map<string, App>();
      
      // Add owner apps first
      ownerApps.forEach(app => uniqueAppsMap.set(app.id, app));
      
      // Add member apps (will overwrite if duplicate - owner takes precedence)
      memberApps.forEach(app => uniqueAppsMap.set(app.id, app));
      
      // Convert Map to array
      const uniqueApps = Array.from(uniqueAppsMap.values());
      
      // Sort by updatedAt
      uniqueApps.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      
      console.log('[subscribeToProjects] 📋 All apps (owner + member):', uniqueApps.map(a => ({ id: a.id, name: a.name })));
      callback(uniqueApps);
    }
  };

  // FIX: Also subscribe to apps where user might be a member (for real-time updates when removed)
  // This ensures we detect when user is removed from members even if invitation is already deleted
  const allAppsQuery = query(
    collection(db, 'apps'),
    orderBy('updatedAt', 'desc')
  );

  let allAppsUnsub: (() => void) | null = null;
  let allAppsReady = false;
  
  // Подписка на owner проекты
  if (userId) {
    ownerUnsub = onSnapshot(ownerQuery, (snapshot) => {
      ownerApps = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        ownerApps.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          icon: data.icon,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          versions: data.versions || [],
          ownerId: data.ownerId || '',
          members: data.members || {},
          isDemo: data.isDemo,
          isOnboarding: data.isOnboarding,
          demoForUserId: data.demoForUserId
        });
      });
      
      console.log('[subscribeToProjects] Owner apps loaded:', ownerApps.length);
      ownerReady = true;
      
      // FIX: Reset callback flag to allow callback after new data arrives
      resetCallbackFlag();
      
      // FIX: Now check if we should call callback (after combining with member apps)
      checkAndCallCallback();
    }, (error) => {
      console.warn('[subscribeToProjects] Owner query error:', error?.code || error?.message);
      ownerReady = true;
      resetCallbackFlag();
      checkAndCallCallback();
    });

    // FIX: Subscribe to ALL apps to detect when user is removed from members
    // This is needed because when owner removes a member, the invitation is deleted
    // and the invitations query won't detect the change
    console.log('[subscribeToProjects] 📡 Firestore Query 2 (all apps for member check): collection=apps');
    
    allAppsUnsub = onSnapshot(allAppsQuery, (snapshot) => {
      console.log('[subscribeToProjects] Processing all apps snapshot, count:', snapshot.size);
      
      const newMemberApps: App[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        
        // Skip if user is owner (already in ownerApps)
        if (data.ownerId === userId) {
          return;
        }
        
        const member = data.members?.[userId];
        const memberStatus = member?.status;
        
        // FIX: Include app only if user is a valid member (active or pending)
        // If status is undefined/null/empty/declined - user was removed
        // Also include if member exists but status is undefined (backwards compatibility for old demos)
        const isValidMember = member && (
          memberStatus === 'active' || 
          memberStatus === 'pending' ||
          memberStatus === undefined ||
          memberStatus === null ||
          memberStatus === ''
        );
        
        console.log('[subscribeToProjects] Checking app:', doc.id, 'isMember:', !!member, 'status:', memberStatus, 'isValid:', isValidMember);
        
        if (isValidMember) {
          newMemberApps.push({
            id: doc.id,
            name: data.name,
            description: data.description,
            icon: data.icon,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            versions: data.versions || [],
            ownerId: data.ownerId || '',
            members: data.members || {},
            isDemo: data.isDemo,
            isOnboarding: data.isOnboarding,
            demoForUserId: data.demoForUserId
          });
        }
      });
      
      // Update member apps
      memberApps = newMemberApps;
      console.log('[subscribeToProjects] Member apps from all apps query:', memberApps.length);
      
      allAppsReady = true;
      invitationsReady = true; // Mark as ready since we use this instead of invitations
      
      resetCallbackFlag();
      checkAndCallCallback();
    }, (error) => {
      console.warn('[subscribeToProjects] All apps query error:', error?.code || error?.message);
      allAppsReady = true;
      invitationsReady = true;
      resetCallbackFlag();
      checkAndCallCallback();
    });

    return () => {
      if (ownerUnsub) ownerUnsub();
      if (invitationsUnsub) invitationsUnsub();
      if (allAppsUnsub) allAppsUnsub();
    };
  }

  // Без userId - возвращаем все проекты
  return onSnapshot(
    query(collection(db, 'apps'), orderBy('updatedAt', 'desc')),
    (snapshot) => {
      const apps: App[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        apps.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          icon: data.icon,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          versions: data.versions || [],
          ownerId: data.ownerId || '',
          members: data.members || {},
          isDemo: data.isDemo,
          isOnboarding: data.isOnboarding,
          demoForUserId: data.demoForUserId
        });
      });
      callback(apps);
    },
    (error) => {
      console.warn('[subscribeToProjects] Error or permission denied:', error?.code || error?.message);
      if (error?.code === 'permission-denied' || error?.code === 'not-found') {
        callback([]);
      }
    }
  );
};

// Создание экрана через Cloud Function
// Примечание: versionFromState используется как fallback, если версия не найдена в Firebase
// Это решает проблему race condition при быстром создании (проект → версия → макет)
export const createScreen = async (
  projectId: string,
  versionId: string,
  data: { name: string; displayName: string; imageUrl?: string },
  user: User,
  versionFromState?: AppVersion
): Promise<{ data: Screen | null; isPending: boolean }> => {
  console.log('[createScreen] Starting with user:', { id: user.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[createScreen] Functions not available');
    return { data: null, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).addScreenSchema as any,
    {
      appId: projectId,
      versionId,
      name: data.name,
      displayName: data.displayName,
      imageUrl: data.imageUrl,
    }
  );

  if (!validation.success) {
    console.error('[createScreen] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const addScreenFn = httpsCallable(functions, 'addScreen');
    const result = await addScreenFn(validation.data);
    
    const response = result.data as { screenId: string; success: boolean };
    
    console.log('[createScreen] Cloud Function response:', response);
    
    if (response.success) {
      console.log('[createScreen] Screen created via Cloud Function:', response.screenId, 'with data:', data);
      
      const newScreen: Screen = {
        id: response.screenId,
        ...data,
        events: []
      };
      
      await logHistory('create', 'screen', response.screenId, projectId, null, { name: data.name, displayName: data.displayName }, user);
      
      return { data: newScreen, isPending: false };
    }
    
    console.error('[createScreen] Cloud Function returned error:', result);
    return { data: null, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[createScreen] Error:', errorMessage);
    if (errorMessage.includes('ліміт') || errorMessage.includes('лимит')) {
      throw new Error(errorMessage);
    }
    throw new Error(errorMessage);
  }
};

// Обновление экрана через Cloud Function
export const updateScreen = async (
  projectId: string,
  versionId: string,
  screenId: string,
  data: Partial<Screen>,
  user: User,
  oldValue?: Screen
): Promise<{ data: boolean; isPending: boolean }> => {
  console.log('[updateScreen] Starting with user:', { id: user.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[updateScreen] Functions not available');
    return { data: false, isPending: false };
  }

  // Build validation data with only provided fields (not undefined)
  // This prevents overwriting existing data with undefined in Firestore
  const validationData: Record<string, any> = {
    appId: projectId,
    versionId: versionId,
    screenId
  };
  
  // Only add fields that are explicitly provided (not undefined)
  if (data.name !== undefined) validationData.name = data.name;
  if (data.displayName !== undefined) validationData.displayName = data.displayName;
  if (data.imageUrl !== undefined) validationData.imageUrl = data.imageUrl;
  if (data.markerScale !== undefined) validationData.markerScale = data.markerScale;
  if (data.popupScale !== undefined) validationData.popupScale = data.popupScale;
  
  console.log('[updateScreen] Sending validation data (only provided fields):', validationData);
  
  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).updateScreenSchema as any,
    validationData
  );

  if (!validation.success) {
    console.error('[updateScreen] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const updateScreenFn = httpsCallable(functions, 'updateScreen');
    const result = await updateScreenFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[updateScreen] Screen updated via Cloud Function:', screenId);
      
      await logHistory('update', 'screen', screenId, projectId, oldValue, data, user);
      
      return { data: true, isPending: false };
    }
    
    console.error('[updateScreen] Cloud Function returned error:', result);
    return { data: false, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[updateScreen] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Удаление экрана через Cloud Function
export const deleteScreen = async (
  projectId: string,
  versionId: string,
  screenId: string,
  user: User
): Promise<{ data: boolean; isPending: boolean }> => {
  // Защитная проверка: если user не передан, возвращаем ошибку
  if (!user || !user.id) {
    console.error('[deleteScreen] User is required but not provided');
    throw new Error('Користувач не авторизований');
  }
  
  console.log('[deleteScreen] Starting with user:', { id: user.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[deleteScreen] Functions not available');
    return { data: false, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).deleteScreenSchema as any,
    {
      appId: projectId,
      versionId,
      screenId,
    }
  );

  if (!validation.success) {
    console.error('[deleteScreen] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const deleteScreenFn = httpsCallable(functions, 'deleteScreen');
    const result = await deleteScreenFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[deleteScreen] Screen deleted via Cloud Function:', screenId);
      
      await logHistory('delete', 'screen', screenId, projectId, { deleted: true }, null, user);
      
      return { data: true, isPending: false };
    }
    
    console.error('[deleteScreen] Cloud Function returned error:', result);
    return { data: false, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[deleteScreen] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Создание события через Cloud Function
export const createEvent = async (
  projectId: string,
  versionId: string,
  screenId: string,
  eventId: string,
  eventData: Omit<AnalyticsEvent, 'id' | 'comments' | 'status'>,
  user: User
): Promise<{ data: AnalyticsEvent | null; isPending: boolean }> => {
  console.log('[createEvent] Starting with user:', { id: user.id, role: user.role });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[createEvent] Functions not available');
    return { data: null, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).addEventSchema as any,
    {
      appId: projectId,
      versionId,
      screenId,
      eventId,
      name: eventData.name,
      type: eventData.type,
      description: eventData.description,
      x: eventData.x,
      y: eventData.y,
      params: eventData.params,
    }
  );

  if (!validation.success) {
    console.error('[createEvent] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const addEventFn = httpsCallable(functions, 'addEvent');
    const result = await addEventFn(validation.data);
    
    const response = result.data as { eventId: string; success: boolean };
    
    if (response.success) {
      console.log('[createEvent] Event created via Cloud Function:', eventId);
      
      const newEvent: AnalyticsEvent = {
        id: eventId,
        name: eventData.name,
        description: eventData.description || '',
        type: eventData.type,
        x: eventData.x,
        y: eventData.y,
        params: eventData.params || [],
        comments: [],
        status: 'new'
      };
      
      await logHistory(
        'create',
        'event',
        eventId,
        projectId,
        null,
        { name: eventData.name, type: eventData.type, description: eventData.description },
        user
      );
      
      return { data: newEvent, isPending: false };
    }
    
    console.error('[createEvent] Cloud Function returned error:', result);
    return { data: null, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[createEvent] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Обновление события через Cloud Function
export const updateEvent = async (
  projectId: string,
  versionId: string,
  screenId: string,
  eventId: string,
  updates: Partial<AnalyticsEvent>,
  user: User,
  oldEvent?: AnalyticsEvent
): Promise<{ data: boolean; isPending: boolean }> => {
  console.log('[updateEvent] Starting with user:', { id: user.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[updateEvent] Functions not available');
    return { data: false, isPending: false };
  }

  // Build validation data with only provided fields (not undefined)
  // This prevents overwriting existing data with undefined in Firestore
  const validationData: Record<string, any> = {
    appId: projectId,
    versionId,
    screenId,
    eventId
  };
  
  // Only add fields that are explicitly provided
  if (updates.name !== undefined) validationData.name = updates.name;
  if (updates.type !== undefined) validationData.type = updates.type;
  if (updates.description !== undefined) validationData.description = updates.description;
  if (updates.x !== undefined) validationData.x = updates.x;
  if (updates.y !== undefined) validationData.y = updates.y;
  if (updates.params !== undefined) validationData.params = updates.params;
  if (updates.status !== undefined) validationData.status = updates.status;
  // FIX: Also include comments when updating
  if (updates.comments !== undefined) validationData.comments = updates.comments;
  // FIX: Include previousValues for tracking edit history
  if (updates.previousValues !== undefined) validationData.previousValues = updates.previousValues;
  if (updates.previousStatus !== undefined) validationData.previousStatus = updates.previousStatus;
  if (updates.lastStatusChangedBy !== undefined) validationData.lastStatusChangedBy = updates.lastStatusChangedBy;
  
  console.log('[updateEvent] Sending validation data (only provided fields):', validationData);
  
  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).updateEventSchema as any,
    validationData
  );

  if (!validation.success) {
    console.error('[updateEvent] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const updateEventFn = httpsCallable(functions, 'updateEvent');
    const result = await updateEventFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[updateEvent] Event updated via Cloud Function:', eventId);
      
      // Prepare relevant fields for history logging
      const relevantOldValue = oldEvent ? {
        name: oldEvent.name,
        type: oldEvent.type,
        description: oldEvent.description,
        status: oldEvent.status,
        params: oldEvent.params
      } : undefined;
      
      const relevantNewValue = {
        name: updates.name,
        type: updates.type,
        description: updates.description,
        status: updates.status,
        params: updates.params
      };
      
      await logHistory('update', 'event', eventId, projectId, relevantOldValue, relevantNewValue, user);
      
      return { data: true, isPending: false };
    }
    
    console.error('[updateEvent] Cloud Function returned error:', result);
    return { data: false, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[updateEvent] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Удаление события через Cloud Function
export const deleteEvent = async (
  projectId: string,
  versionId: string,
  screenId: string,
  eventId: string,
  user: User
): Promise<{ data: boolean; isPending: boolean }> => {
  console.log('[deleteEvent] Starting with user:', { id: user.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[deleteEvent] Functions not available');
    return { data: false, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).deleteEventSchema as any,
    {
      appId: projectId,
      versionId,
      screenId,
      eventId,
    }
  );

  if (!validation.success) {
    console.error('[deleteEvent] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const deleteEventFn = httpsCallable(functions, 'deleteEvent');
    const result = await deleteEventFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[deleteEvent] Event deleted via Cloud Function:', eventId);
      
      await logHistory('delete', 'event', eventId, projectId, { deleted: true }, null, user);
      
      return { data: true, isPending: false };
    }
    
    console.error('[deleteEvent] Cloud Function returned error:', result);
    return { data: false, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[deleteEvent] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Добавление версии через Cloud Function
export const addVersion = async (
  projectId: string,
  version: string,
  name: string,
  user: User,
  copyScreens: boolean = true
): Promise<{ data: AppVersion | null; isPending: boolean }> => {
  console.log('[addVersion] Starting with user:', { id: user.id }, 'copyScreens:', copyScreens);

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[addVersion] Functions not available');
    return { data: null, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).addVersionSchema as any,
    {
      appId: projectId,
      version,
      name,
      copyScreens,
    }
  );

  if (!validation.success) {
    console.error('[addVersion] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const addVersionFn = httpsCallable(functions, 'addVersion');
    const result = await addVersionFn(validation.data);
    
    const response = result.data as { versionId: string; success: boolean; version?: AppVersion };
    
    if (response.success) {
      console.log('[addVersion] Version created via Cloud Function:', response.versionId);
      
      // Если Cloud Function вернула версию с экранами - используем её
      // Иначе создаём локальную пустую версию
      let newVersion: AppVersion;
      if (response.version && response.version.screens) {
        newVersion = {
          id: response.versionId,
          version,
          name,
          releaseDate: response.version.releaseDate || new Date().toISOString(),
          isActive: true,
          screens: response.version.screens,
          eventCount: response.version.eventCount || 0
        };
        console.log('[addVersion] Using version with screens from Cloud Function:', newVersion.screens.length);
      } else {
        newVersion = {
          id: response.versionId,
          version,
          name,
          releaseDate: new Date().toISOString(),
          isActive: true,
          screens: [],
          eventCount: 0
        };
        console.log('[addVersion] No screens in response, using empty version');
      }
      
      await logHistory('create', 'version', response.versionId, projectId, null, { version, name }, user);
      
      return { data: newVersion, isPending: false };
    }
    
    console.error('[addVersion] Cloud Function returned error:', result);
    return { data: null, isPending: false };
  } catch (error: any) {
    console.log('[addVersion] Raw Firebase error:', error);
    console.log('[addVersion] Error code:', error?.code);
    console.log('[addVersion] Error message:', error?.message);
    console.log('[addVersion] Full error:', JSON.stringify(error, null, 2));
    const errorMessage = handleFirebaseError(error);
    console.log('[addVersion] After handleFirebaseError:', errorMessage);
    console.error('[addVersion] Error:', errorMessage);
    if (errorMessage.includes('ліміт') || errorMessage.includes('лимит')) {
      throw new Error(errorMessage);
    }
    throw new Error(errorMessage);
  }
};

// Удаление версии через Cloud Function
export const deleteVersion = async (
  projectId: string,
  versionId: string,
  user: User
): Promise<{ data: boolean; isPending: boolean }> => {
  console.log('[deleteVersion] Starting with user:', { id: user.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[deleteVersion] Functions not available');
    return { data: false, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).deleteVersionSchema as any,
    {
      appId: projectId,
      versionId,
    }
  );

  if (!validation.success) {
    console.error('[deleteVersion] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const deleteVersionFn = httpsCallable(functions, 'deleteVersion');
    const result = await deleteVersionFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[deleteVersion] Version deleted via Cloud Function:', versionId);
      
      await logHistory('delete', 'version', versionId, projectId, { deleted: true }, null, user);
      
      return { data: true, isPending: false };
    }
    
    console.error('[deleteVersion] Cloud Function returned error:', result);
    return { data: false, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[deleteVersion] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Обновление версии через Cloud Function
export const updateVersion = async (
  projectId: string,
  versionId: string,
  data: { version?: string; name?: string },
  user: User,
  oldValue?: AppVersion
): Promise<{ data: boolean; isPending: boolean }> => {
  console.log('[updateVersion] Starting with user:', { id: user.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[updateVersion] Functions not available');
    return { data: false, isPending: false };
  }

  // Build validation data with only provided fields (not undefined)
  // This prevents overwriting existing data with undefined in Firestore
  const validationData: Record<string, any> = {
    appId: projectId,
    versionId
  };
  
  // Only add fields that are explicitly provided
  if (data.version !== undefined) validationData.version = data.version;
  if (data.name !== undefined) validationData.name = data.name;
  
  console.log('[updateVersion] Sending validation data (only provided fields):', validationData);
  
  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).updateVersionSchema as any,
    validationData
  );

  if (!validation.success) {
    console.error('[updateVersion] Validation error:', validation.error);
    throw new Error(validation.error || 'Помилка валідації');
  }

  try {
    const updateVersionFn = httpsCallable(functions, 'updateVersion');
    const result = await updateVersionFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[updateVersion] Version updated via Cloud Function:', versionId);
      
      await logHistory('update', 'version', versionId, projectId, oldValue, data, user);
      
      return { data: true, isPending: false };
    }
    
    console.error('[updateVersion] Cloud Function returned error:', result);
    return { data: false, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[updateVersion] Error:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Получение истории проекта
export const getHistory = async (projectId: string, limitCount: number = 50): Promise<HistoryRecord[]> => {
  if (!isFirebaseAvailable()) return [];

  const db = getDbInstance();
  if (!db) return [];

  try {
    const q = query(
      collection(db, 'history'),
      where('projectId', '==', projectId),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const history: HistoryRecord[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      history.push({
        id: doc.id,
        projectId: data.projectId,
        action: data.action,
        entityType: data.entityType,
        entityId: data.entityId,
        entityName: data.entityName,
        userId: data.userId,
        userName: data.userName,
        userRole: data.userRole,
        // userAvatar теперь не хранится - получаем динамически
        // userAvatar: data.userAvatar || undefined,
        oldValue: data.oldValue ? (typeof data.oldValue === 'string' ? JSON.parse(data.oldValue) : data.oldValue) : undefined,
        newValue: data.newValue ? (typeof data.newValue === 'string' ? JSON.parse(data.newValue) : data.newValue) : undefined,
        timestamp: data.timestamp?.toDate?.() || new Date(),
        description: data.description
      });
    });
    
    return history;
  } catch (error) {
    console.error('Error getting history:', error);
    return [];
  }
};

// Подписка на историю
export const subscribeToHistory = (projectId: string, currentUserId: string, callback: (history: HistoryRecord[]) => void) => {
  console.log('[subscribeToHistory] Starting subscription for project:', projectId, 'user:', currentUserId);
  
  if (!isFirebaseAvailable()) {
    console.warn('[subscribeToHistory] Firebase not available');
    callback([]);
    return () => {};
  }

  const db = getDbInstance();
  if (!db) {
    console.warn('[subscribeToHistory] DB not available');
    callback([]);
    return () => {};
  }

  return onSnapshot(
    query(
      collection(db, 'history'),
      where('projectId', '==', projectId),
      // Фильтр по allowedUsers для соответствия правилам
      where('allowedUsers', 'array-contains', currentUserId),
      orderBy('timestamp', 'desc'),
      firestoreLimit(100)
    ),
    (snapshot) => {
      console.log('[subscribeToHistory] Received snapshot, docs count:', snapshot.size);
      const history: HistoryRecord[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        history.push({
          id: doc.id,
          projectId: data.projectId,
          action: data.action,
          entityType: data.entityType,
          entityId: data.entityId,
          entityName: data.entityName,
          userId: data.userId,
          userName: data.userName,
          userRole: data.userRole,
          oldValue: data.oldValue ? (typeof data.oldValue === 'string' ? JSON.parse(data.oldValue) : data.oldValue) : undefined,
          newValue: data.newValue ? (typeof data.newValue === 'string' ? JSON.parse(data.newValue) : data.newValue) : undefined,
          timestamp: data.timestamp?.toDate?.() || new Date(),
          description: data.description
        });
      });
      console.log('[subscribeToHistory] Parsed history records:', history.length);
      callback(history);
    },
    (error) => {
      console.warn('[subscribeToHistory] Error or permission denied:', error?.code || error?.message);
      if (error?.code === 'permission-denied' || error?.code === 'not-found') {
        callback([]);
      }
    }
  );
};
  
export { isFirebaseAvailable };

// ============================================
// SUB-COLLECTION SERVICES (Версии, Экраны, События как подколлекции)
// ============================================

// Генерирует уникальный ID для подколлекций
const generateSubId = () => Math.random().toString(36).substring(2, 15) + Date.now().toString(36);

// ============================================================================
// VERSIONS SUB-COLLECTION: apps/{appId}/versions/{versionId}
// ============================================================================

export interface VersionDocData {
  id: string;
  version: string;
  name: string;
  projectId: string;
  ownerId: string;
  isActive: boolean;
  releaseDate?: string;
  eventCount?: number;
  createdAt: ReturnType<typeof serverTimestamp>;
  updatedAt?: ReturnType<typeof serverTimestamp>;
}

/**
 * Создание версии как подколлекции
 * @param projectId - ID проекта (app)
 * @param version - номер версии (например, "1.0.0")
 * @param name - название версии (например, "Production")
 * @param user - текущий пользователь
 */
export const createVersion = async (
  projectId: string,
  version: string,
  name: string,
  user: User
): Promise<{ id: string; version: string; name: string } | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  // Проверка лимита версий
  try {
    const versionsSnapshot = await getDocs(collection(db, 'apps', projectId, 'versions'));
    const limits = await getUserLimits(user.id);
    
    if (versionsSnapshot.size >= limits.maxVersions) {
      console.error('[createVersion] Limit reached:', { current: versionsSnapshot.size, max: limits.maxVersions });
      throw new Error(`Досягнуто ліміт версій (${limits.maxVersions}). Видаліть існуючу версію або оновіть тариф.`);
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('ліміт')) {
      throw error;
    }
  }

  const versionId = generateSubId();
  const now = new Date().toISOString();

  try {
    // Делаем все существующие версии неактивными
    const versionsSnapshot = await getDocs(collection(db, 'apps', projectId, 'versions'));
    const batch = writeBatch(db);
    
    // Деактивируем все существующие версии
    versionsSnapshot.forEach((doc) => {
      batch.update(doc.ref, { isActive: false });
    });
    
    // Создаём новую версию как подколлекцию
    const versionData: VersionDocData = {
      id: versionId,
      version,
      name,
      projectId,
      ownerId: user.id,
      isActive: true,
      releaseDate: now,
      eventCount: 0,
      createdAt: serverTimestamp()
    };
    
    batch.set(doc(db, 'apps', projectId, 'versions', versionId), versionData);
    
    await batch.commit();
    
    await logHistory('create', 'version', versionId, projectId, null, { version, name }, user);
    
    console.log('[createVersion] Created version:', versionId, version, name);
    return { id: versionId, version, name };
  } catch (error) {
    console.error('[createVersion] Error:', error);
    return null;
  }
};

/**
 * Получение всех версий проекта из подколлекции
 */
export const getVersions = async (projectId: string): Promise<VersionDocData[]> => {
  if (!isFirebaseAvailable()) return [];

  const db = getDbInstance();
  if (!db) return [];

  try {
    const snapshot = await getDocs(
      query(collection(db, 'apps', projectId, 'versions'), orderBy('createdAt', 'desc'))
    );
    
    const versions: VersionDocData[] = [];
    snapshot.forEach((doc) => {
      versions.push(doc.data() as VersionDocData);
    });
    
    return versions;
  } catch (error) {
    console.error('[getVersions] Error:', error);
    return [];
  }
};

/**
 * Получение одной версии по ID
 */
export const getVersion = async (projectId: string, versionId: string): Promise<VersionDocData | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  try {
    const docSnap = await getDoc(doc(db, 'apps', projectId, 'versions', versionId));
    
    if (docSnap.exists()) {
      return docSnap.data() as VersionDocData;
    }
    return null;
  } catch (error) {
    console.error('[getVersion] Error:', error);
    return null;
  }
};

/**
 * Обновление версии
 */
export const updateVersionSub = async (
  projectId: string,
  versionId: string,
  data: { version?: string; name?: string; isActive?: boolean },
  user: User
): Promise<boolean> => {
  if (!isFirebaseAvailable()) return false;

  const db = getDbInstance();
  if (!db) return false;

  try {
    // Получаем старые данные версии перед обновлением
    const oldVersionDoc = await getDoc(doc(db, 'apps', projectId, 'versions', versionId));
    const oldVersionData = oldVersionDoc.exists() ? oldVersionDoc.data() : null;

    // Если активируем версию - деактивируем все остальные
    if (data.isActive === true) {
      const versionsSnapshot = await getDocs(collection(db, 'apps', projectId, 'versions'));
      const batch = writeBatch(db);
      
      versionsSnapshot.forEach((vDoc) => {
        if (vDoc.id !== versionId) {
          batch.update(vDoc.ref, { isActive: false });
        }
      });
      
      batch.update(doc(db, 'apps', projectId, 'versions', versionId), {
        ...data,
        updatedAt: serverTimestamp()
      });
      
      await batch.commit();
    } else {
      await updateDoc(doc(db, 'apps', projectId, 'versions', versionId), {
        ...data,
        updatedAt: serverTimestamp()
      });
    }

    await logHistory('update', 'version', versionId, projectId, oldVersionData, data, user);
    return true;
  } catch (error) {
    console.error('[updateVersionSub] Error:', error);
    return false;
  }
};

/**
 * Удаление версии
 */
export const deleteVersionSub = async (
  projectId: string,
  versionId: string,
  user: User
): Promise<boolean> => {
  if (!isFirebaseAvailable()) return false;

  const db = getDbInstance();
  if (!db) return false;

  try {
    // Проверяем количество версий
    const versionsSnapshot = await getDocs(collection(db, 'apps', projectId, 'versions'));
    
    if (versionsSnapshot.size <= 1) {
      console.warn('[deleteVersionSub] Cannot delete the last version');
      return false;
    }
    
    // Проверяем, является ли версия активной
    const versionDoc = await getDoc(doc(db, 'apps', projectId, 'versions', versionId));
    if (!versionDoc.exists()) return false;
    
    const versionData = versionDoc.data() as VersionDocData;
    
    // Удаляем версию
    await deleteDoc(doc(db, 'apps', projectId, 'versions', versionId));
    
    // Если удалили активную версию, делаем первую активной
    if (versionData.isActive) {
      const remainingVersions = await getDocs(collection(db, 'apps', projectId, 'versions'));
      if (!remainingVersions.empty) {
        const firstVersion = remainingVersions.docs[0];
        await updateDoc(firstVersion.ref, { isActive: true });
      }
    }
    
    await logHistory('delete', 'version', versionId, projectId, { deleted: true }, null, user);
    return true;
  } catch (error) {
    console.error('[deleteVersionSub] Error:', error);
    return false;
  }
};

/**
 * Подписка на изменения версий (оптимизированная)
 * Использует фильтры для правил безопасности и пагинацию
 */
export const subscribeToVersions = (
  projectId: string,
  callback: (versions: VersionDocData[]) => void
) => {
  if (!isFirebaseAvailable()) {
    callback([]);
    return () => {};
  }

  const db = getDbInstance();
  if (!db) {
    callback([]);
    return () => {};
  }

  return onSnapshot(
    query(
      collection(db, 'apps', projectId, 'versions'),
      where('createdAt', '!=', null), // Фильтр для правил безопасности
      orderBy('createdAt', 'desc'),
      firestoreLimit(100) // Пагинация
    ),
    (snapshot) => {
      const versions: VersionDocData[] = [];
      snapshot.forEach((doc) => {
        versions.push(doc.data() as VersionDocData);
      });
      callback(versions);
    },
    (error) => {
      console.warn('[subscribeToVersions] Error:', error?.code || error?.message);
      if (error?.code === 'permission-denied' || error?.code === 'not-found') {
        callback([]);
      }
    }
  );
};

// ============================================================================
// SCREENS SUB-COLLECTION: apps/{appId}/screens/{screenId}
// ============================================================================

export interface ScreenDocData {
  id: string;
  name: string;
  displayName: string;
  imageUrl?: string;
  projectId: string;
  ownerId: string;
  versionId?: string; // опционально - связь с версией
  markerScale?: number;
  popupScale?: number;
  createdAt: ReturnType<typeof serverTimestamp>;
  updatedAt?: ReturnType<typeof serverTimestamp>;
}

/**
 * Создание экрана как подколлекции
 */
export const createScreenAsEntity = async (
  projectId: string,
  data: { name: string; displayName: string; imageUrl?: string; versionId?: string },
  user: User
): Promise<ScreenDocData | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  // Проверка лимита экранов
  try {
    const screensSnapshot = await getDocs(collection(db, 'apps', projectId, 'screens'));
    const limits = await getUserLimits(user.id);
    
    if (screensSnapshot.size >= limits.maxScreens) {
      console.error('[createScreenAsEntity] Limit reached:', { current: screensSnapshot.size, max: limits.maxScreens });
      throw new Error(`Досягнуто ліміт екранів (${limits.maxScreens}). Видаліть існуючий екран або оновіть тариф.`);
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('ліміт')) {
      throw error;
    }
  }

  const screenId = generateSubId();

  try {
    const screenData: ScreenDocData = {
      id: screenId,
      name: data.name,
      displayName: data.displayName,
      imageUrl: data.imageUrl,
      projectId,
      ownerId: user.id,
      versionId: data.versionId,
      markerScale: 1,
      createdAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'apps', projectId, 'screens', screenId), screenData);
    
    await logHistory('create', 'screen', screenId, projectId, null, { name: data.name, displayName: data.displayName }, user);
    
    console.log('[createScreenAsEntity] Created screen:', screenId, data.name);
    return screenData;
  } catch (error) {
    console.error('[createScreenAsEntity] Error:', error);
    return null;
  }
};

/**
 * Получение всех экранов проекта из подколлекции
 */
export const getScreens = async (projectId: string): Promise<ScreenDocData[]> => {
  if (!isFirebaseAvailable()) return [];

  const db = getDbInstance();
  if (!db) return [];

  try {
    const snapshot = await getDocs(collection(db, 'apps', projectId, 'screens'));
    
    const screens: ScreenDocData[] = [];
    snapshot.forEach((doc) => {
      screens.push(doc.data() as ScreenDocData);
    });
    
    return screens;
  } catch (error) {
    console.error('[getScreens] Error:', error);
    return [];
  }
};

/**
 * Получение одного экрана по ID
 */
export const getScreen = async (projectId: string, screenId: string): Promise<ScreenDocData | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  try {
    const docSnap = await getDoc(doc(db, 'apps', projectId, 'screens', screenId));
    
    if (docSnap.exists()) {
      return docSnap.data() as ScreenDocData;
    }
    return null;
  } catch (error) {
    console.error('[getScreen] Error:', error);
    return null;
  }
};

/**
 * Обновление экрана
 */
export const updateScreenAsEntity = async (
  projectId: string,
  screenId: string,
  data: Partial<ScreenDocData>,
  user: User
): Promise<boolean> => {
  if (!isFirebaseAvailable()) return false;

  const db = getDbInstance();
  if (!db) return false;

  try {
    // Получаем старые данные экрана перед обновлением
    const oldScreenDoc = await getDoc(doc(db, 'apps', projectId, 'screens', screenId));
    const oldScreenData = oldScreenDoc.exists() ? oldScreenDoc.data() : null;

    await updateDoc(doc(db, 'apps', projectId, 'screens', screenId), {
      ...data,
      updatedAt: serverTimestamp()
    });

    await logHistory('update', 'screen', screenId, projectId, oldScreenData, data, user);
    return true;
  } catch (error) {
    console.error('[updateScreenAsEntity] Error:', error);
    return false;
  }
};

/**
 * Удаление экрана
 */
export const deleteScreenAsEntity = async (
  projectId: string,
  screenId: string,
  user: User
): Promise<boolean> => {
  if (!isFirebaseAvailable()) return false;

  const db = getDbInstance();
  if (!db) return false;

  try {
    await deleteDoc(doc(db, 'apps', projectId, 'screens', screenId));
    
    await logHistory('delete', 'screen', screenId, projectId, { deleted: true }, null, user);
    return true;
  } catch (error) {
    console.error('[deleteScreenAsEntity] Error:', error);
    return false;
  }
};

/**
 * Подписка на изменения экранов (оптимизированная)
 * Использует фильтры для правил безопасности и пагинацию
 */
export const subscribeToScreens = (
  projectId: string,
  callback: (screens: ScreenDocData[]) => void
) => {
  if (!isFirebaseAvailable()) {
    callback([]);
    return () => {};
  }

  const db = getDbInstance();
  if (!db) {
    callback([]);
    return () => {};
  }

  return onSnapshot(
    query(
      collection(db, 'apps', projectId, 'screens'),
      where('createdAt', '!=', null), // Фильтр для правил безопасности
      orderBy('createdAt', 'desc'),
      firestoreLimit(100) // Пагинация
    ),
    (snapshot) => {
      const screens: ScreenDocData[] = [];
      snapshot.forEach((doc) => {
        screens.push(doc.data() as ScreenDocData);
      });
      callback(screens);
    },
    (error) => {
      console.warn('[subscribeToScreens] Error:', error?.code || error?.message);
      if (error?.code === 'permission-denied' || error?.code === 'not-found') {
        callback([]);
      }
    }
  );
};

// ============================================================================
// EVENTS SUB-COLLECTION: apps/{appId}/events/{eventId}
// ============================================================================

export interface EventDocData {
  id: string;
  name: string;
  type: EventType;
  description?: string;
  x: number;
  y: number;
  params: EventParam[];
  status: EventStatus;
  projectId: string;
  ownerId: string;
  screenId?: string; // опционально - связь с экраном
  versionId?: string; // опционально - связь с версией
  previousVersionId?: string;
  implemented?: boolean;
  lastStatusChangedBy?: string;
  comments: Comment[];
  createdAt: ReturnType<typeof serverTimestamp>;
  updatedAt?: ReturnType<typeof serverTimestamp>;
}

/**
 * Создание события как подколлекции
 */
export const createEventAsEntity = async (
  projectId: string,
  data: {
    name: string;
    type: EventType;
    description?: string;
    x: number;
    y: number;
    params?: EventParam[];
    screenId?: string;
    versionId?: string;
  },
  user: User
): Promise<EventDocData | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  // Проверка лимита событий
  try {
    const eventsSnapshot = await getDocs(collection(db, 'apps', projectId, 'events'));
    const limits = await getUserLimits(user.id);
    
    if (eventsSnapshot.size >= limits.maxEvents) {
      console.error('[createEventAsEntity] Limit reached:', { current: eventsSnapshot.size, max: limits.maxEvents });
      throw new Error(`Досягнуто ліміт подій (${limits.maxEvents}). Видаліть існуючу подію або оновіть тариф.`);
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('ліміт')) {
      throw error;
    }
  }

  const eventId = generateSubId();

  try {
    const eventData: EventDocData = {
      id: eventId,
      name: data.name,
      type: data.type,
      description: data.description || '',
      x: data.x,
      y: data.y,
      params: data.params || [],
      status: 'new',
      projectId,
      ownerId: user.id,
      screenId: data.screenId,
      versionId: data.versionId,
      comments: [],
      createdAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'apps', projectId, 'events', eventId), eventData);
    
    await logHistory(
      'create',
      'event',
      eventId,
      projectId,
      null,
      { name: data.name, type: data.type, description: data.description },
      user
    );
    
    console.log('[createEventAsEntity] Created event:', eventId, data.name);
    return eventData;
  } catch (error) {
    console.error('[createEventAsEntity] Error:', error);
    return null;
  }
};

/**
 * Получение всех событий проекта из подколлекции
 */
export const getEvents = async (projectId: string): Promise<EventDocData[]> => {
  if (!isFirebaseAvailable()) return [];

  const db = getDbInstance();
  if (!db) return [];

  try {
    const snapshot = await getDocs(collection(db, 'apps', projectId, 'events'));
    
    const events: EventDocData[] = [];
    snapshot.forEach((doc) => {
      events.push(doc.data() as EventDocData);
    });
    
    return events;
  } catch (error) {
    console.error('[getEvents] Error:', error);
    return [];
  }
};

/**
 * Получение событий для конкретного экрана
 */
export const getEventsByScreen = async (projectId: string, screenId: string): Promise<EventDocData[]> => {
  if (!isFirebaseAvailable()) return [];

  const db = getDbInstance();
  if (!db) return [];

  try {
    const snapshot = await getDocs(
      query(
        collection(db, 'apps', projectId, 'events'),
        where('screenId', '==', screenId)
      )
    );
    
    const events: EventDocData[] = [];
    snapshot.forEach((doc) => {
      events.push(doc.data() as EventDocData);
    });
    
    return events;
  } catch (error) {
    console.error('[getEventsByScreen] Error:', error);
    return [];
  }
};

/**
 * Получение одного события по ID
 */
export const getEvent = async (projectId: string, eventId: string): Promise<EventDocData | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  try {
    const docSnap = await getDoc(doc(db, 'apps', projectId, 'events', eventId));
    
    if (docSnap.exists()) {
      return docSnap.data() as EventDocData;
    }
    return null;
  } catch (error) {
    console.error('[getEvent] Error:', error);
    return null;
  }
};

/**
 * Обновление события
 */
export const updateEventAsEntity = async (
  projectId: string,
  eventId: string,
  data: Partial<EventDocData>,
  user: User
): Promise<boolean> => {
  if (!isFirebaseAvailable()) return false;

  const db = getDbInstance();
  if (!db) return false;

  try {
    // Получаем старые данные события перед обновлением
    const oldEventDoc = await getDoc(doc(db, 'apps', projectId, 'events', eventId));
    const oldEventData = oldEventDoc.exists() ? oldEventDoc.data() : null;

    await updateDoc(doc(db, 'apps', projectId, 'events', eventId), {
      ...data,
      updatedAt: serverTimestamp()
    });

    await logHistory('update', 'event', eventId, projectId, oldEventData, data, user);
    return true;
  } catch (error) {
    console.error('[updateEventAsEntity] Error:', error);
    return false;
  }
};

/**
 * Удаление события
 */
export const deleteEventAsEntity = async (
  projectId: string,
  eventId: string,
  user: User
): Promise<boolean> => {
  if (!isFirebaseAvailable()) return false;

  const db = getDbInstance();
  if (!db) return false;

  try {
    await deleteDoc(doc(db, 'apps', projectId, 'events', eventId));
    
    await logHistory('delete', 'event', eventId, projectId, { deleted: true }, null, user);
    return true;
  } catch (error) {
    console.error('[deleteEventAsEntity] Error:', error);
    return false;
  }
};

/**
 * Подписка на изменения событий (оптимизированная)
 * Использует фильтры для правил безопасности и пагинацию
 */
export const subscribeToEvents = (
  projectId: string,
  callback: (events: EventDocData[]) => void
) => {
  if (!isFirebaseAvailable()) {
    callback([]);
    return () => {};
  }

  const db = getDbInstance();
  if (!db) {
    callback([]);
    return () => {};
  }

  return onSnapshot(
    query(
      collection(db, 'apps', projectId, 'events'),
      where('createdAt', '!=', null), // Фильтр для правил безопасности
      orderBy('createdAt', 'desc'),
      firestoreLimit(100) // Пагинация
    ),
    (snapshot) => {
      const events: EventDocData[] = [];
      snapshot.forEach((doc) => {
        events.push(doc.data() as EventDocData);
      });
      callback(events);
    },
    (error) => {
      console.warn('[subscribeToEvents] Error:', error?.code || error?.message);
      if (error?.code === 'permission-denied' || error?.code === 'not-found') {
        callback([]);
      }
    }
  );
};

/**
 * Подписка на события конкретного экрана
 */
export const subscribeToEventsByScreen = (
  projectId: string,
  screenId: string,
  callback: (events: EventDocData[]) => void
) => {
  if (!isFirebaseAvailable()) {
    callback([]);
    return () => {};
  }

  const db = getDbInstance();
  if (!db) {
    callback([]);
    return () => {};
  }

  return onSnapshot(
    query(collection(db, 'apps', projectId, 'events'), where('screenId', '==', screenId)),
    (snapshot) => {
      const events: EventDocData[] = [];
      snapshot.forEach((doc) => {
        events.push(doc.data() as EventDocData);
      });
      callback(events);
    },
    (error) => {
      console.warn('[subscribeToEventsByScreen] Error:', error?.code || error?.message);
      if (error?.code === 'permission-denied' || error?.code === 'not-found') {
        callback([]);
      }
    }
  );
};

// ============================================
// Управление доступом к приложениям
// ============================================

// Поиск пользователя по email
const findUserByEmail = async (email: string): Promise<{ id: string; name: string; email: string } | null> => {
  if (!isFirebaseAvailable()) return null;

  const db = getDbInstance();
  if (!db) return null;

  try {
    const q = query(collection(db, 'users'), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const data = userDoc.data();
      return {
        id: userDoc.id,
        name: data.name || data.displayName || email.split('@')[0],
        email: data.email || email
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error finding user by email:', error);
    return null;
  }
};

// Добавление участника к приложению через Cloud Function
// С статусом pending - требует подтверждения
// ТАКЖЕ добавляет пользователя в members проекта немедленно
// FIX: Теперь при создании приглашения пользователь сразу добавляется в members
//       со статусом 'pending', что даёт ему доступ к проекту через subscribeToProjects
//       Это устраняет race condition и проблему с отсутствием проекта в списке
//       после принятия приглашения

export const inviteUser = async (
  appId: string,
  memberEmail: string,
  role: string,
  currentUser: User
): Promise<{ success: boolean; error?: string }> => {
  console.log('[inviteUser] ===== START =====');
  console.log('[inviteUser] Input params:', { appId, memberEmail, role, currentUserId: currentUser.id });
  
  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[inviteUser] Functions not available');
    return { success: false, error: 'Firebase Functions not available' };
  }

  // FIX: Сначала пробуем найти пользователя по email для немедленного добавления в members
  let invitedUserId: string | null = null;
  const db = getDbInstance();
  
  if (db) {
    try {
      const foundUser = await findUserByEmail(memberEmail);
      if (foundUser) {
        invitedUserId = foundUser.id;
        console.log('[inviteUser] Found user by email:', { userId: invitedUserId, email: foundUser.email, name: foundUser.name });
      } else {
        console.log('[inviteUser] User not found by email, will add after registration');
      }
    } catch (error) {
      console.warn('[inviteUser] Error finding user by email:', error);
    }
  }

  try {
    const inviteUserFn = httpsCallable(functions, 'inviteUser');
    const result = await inviteUserFn({
      appId,
      email: memberEmail,
      role
    });
    
    const response = result.data as { invitationId: string; success: boolean; invitedUserId?: string };
    
    // FIX: Если Cloud Function вернул userId, используем его
    if (response.invitedUserId && !invitedUserId) {
      invitedUserId = response.invitedUserId;
      console.log('[inviteUser] Got userId from Cloud Function:', invitedUserId);
    }
    
    if (response.success) {
      console.log('[inviteUser] User invited via Cloud Function:', memberEmail);
      
      // FIX: Добавляем пользователя в members проекта со статусом 'pending'
      // Это даёт пользователю доступ к проекту сразу после создания приглашения
      if (db && invitedUserId) {
        try {
          const memberPath = `members.${invitedUserId}`;
          console.log('[inviteUser] Adding user to members with pending status:', { memberPath, role });
          
          await updateDoc(doc(db, 'apps', appId), {
            [memberPath]: {
              role: role,
              status: 'pending',
              addedAt: serverTimestamp(),
              email: memberEmail,
              name: memberEmail.split('@')[0],
              invitedBy: currentUser.id
            },
            updatedAt: serverTimestamp()
          });
          
          console.log('[inviteUser] Successfully added user to members:', invitedUserId);
        } catch (memberError) {
          console.error('[inviteUser] Error adding user to members:', memberError);
          // Не возвращаем ошибку - приглашение уже создано
        }
      } else {
        console.log('[inviteUser] Skipping adding to members - no userId (user not registered yet)');
      }
      
      await logHistory('update', 'project', appId, appId, null, { addedMember: memberEmail, role, status: 'pending' }, currentUser);
      console.log('[inviteUser] ===== SUCCESS =====');
      return { success: true };
    }
    
    console.error('[inviteUser] Cloud Function returned error:', result);
    return { success: false, error: 'Failed to invite user' };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    showError(error, 'inviteUser');
    return { success: false, error: errorMessage };
  }
};

// Alias для обратной совместимости
export const addMember = inviteUser;

// Принятие приглашения (подтверждение доступа)
export const acceptInvitation = async (
  invitationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  console.log('[acceptInvitation] ===== START =====');
  console.log('[acceptInvitation] Input params:', { invitationId, userId });
  
  if (!isFirebaseAvailable()) {
    console.log('[acceptInvitation] Firebase not available');
    return { success: false, error: 'Firebase not available' };
  }

  const db = getDbInstance();
  if (!db) {
    console.log('[acceptInvitation] DB not available');
    return { success: false, error: 'Firebase not available' };
  }

  try {
    // Пробуем найти приглашение по переданному ID
    console.log('[acceptInvitation] Trying to fetch invitation by ID:', invitationId);
    let invitationDoc = await getDoc(doc(db, 'invitations', invitationId));
    
    // Если не найдено по ID, пробуем найти по шаблону {appId}_{userId}
    if (!invitationDoc.exists()) {
      console.log('[acceptInvitation] Invitation not found by direct ID, trying appId_userId pattern');
      // Пробуем найти по шаблону appId_userId
      const parts = invitationId.split('_');
      if (parts.length >= 2) {
        // invitationId может быть appId - пробуем искать с постфиксом userId
        const alternateId = `${invitationId}_${userId}`;
        console.log('[acceptInvitation] Trying alternate ID:', alternateId);
        invitationDoc = await getDoc(doc(db, 'invitations', alternateId));
      }
    }
    
    // Если всё ещё не найдено, пробуем искать среди всех приглашений для пользователя
    if (!invitationDoc.exists()) {
      console.log('[acceptInvitation] Searching for invitation by userId in all pending invitations');
      const allInvitations = await getDocs(query(
        collection(db, 'invitations'),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      ));
      
      // Ищем приглашение для этого appId
      for (const docSnap of allInvitations.docs) {
        const data = docSnap.data();
        if (data.appId === invitationId || docSnap.id.includes(invitationId)) {
          console.log('[acceptInvitation] Found invitation by search:', docSnap.id);
          invitationDoc = docSnap;
          break;
        }
      }
    }
    
    if (!invitationDoc.exists()) {
      console.log('[acceptInvitation] Invitation not found with any method');
      return { success: false, error: 'Запрошення не знайдено' };
    }
    
    const invitationData = invitationDoc.data();
    const realInvitationId = invitationDoc.id; // Используем реальный ID документа
    console.log('[acceptInvitation] Invitation found, real ID:', realInvitationId, 'data:', { status: invitationData.status, appId: invitationData.appId });
    
    if (invitationData.status !== 'pending') {
      console.log('[acceptInvitation] Invitation not pending, status:', invitationData.status);
      return { success: false, error: 'Запрошення не знайдено або вже оброблено' };
    }

    const appId = invitationData.appId;
    
    // Получаем проект
    console.log('[acceptInvitation] Fetching app:', appId);
    const appDoc = await getDoc(doc(db, 'apps', appId));
    if (!appDoc.exists()) {
      console.log('[acceptInvitation] App not found');
      return { success: false, error: 'Додаток не знайдено' };
    }

    const appData = appDoc.data();
    console.log('[acceptInvitation] App data:', { ownerId: appData.ownerId, memberUserId: userId, memberData: appData.members?.[userId] });
    const members = appData.members || {};

    console.log('[acceptInvitation] About to update app members. Current members:', Object.keys(members));
    console.log('[acceptInvitation] User being added:', { userId, newStatus: 'active', role: invitationData.role });

    // Используем dot notation для обновления только статуса
    // Это сохраняет все существующие данные участника (role, email, name, invitedBy, addedAt)
    const memberStatusPath = `members.${userId}.status`;
    const memberConfirmedAtPath = `members.${userId}.confirmedAt`;
    console.log('[acceptInvitation] Using dot notation to update status:', memberStatusPath);
    
    await updateDoc(doc(db, 'apps', appId), {
      [memberStatusPath]: 'active',
      [memberConfirmedAtPath]: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log('[acceptInvitation] updateDoc for apps/ succeeded (updated status to active using dot notation)');

    // Обновляем статус приглашения на 'accepted' (НЕ удаляем, чтобы подписка работала)
    console.log('[acceptInvitation] Updating invitation status to accepted, ID:', realInvitationId);
    await updateDoc(doc(db, 'invitations', realInvitationId), {
      status: 'accepted',
      acceptedAt: new Date().toISOString()
    });
    console.log('[acceptInvitation] updateDoc for invitations succeeded');
    console.log('[acceptInvitation] ===== SUCCESS =====');

    return { success: true };
  } catch (error: any) {
    console.error('[acceptInvitation] Error:', error?.code, error?.message);
    return { success: false, error: 'Помилка при підтвердженні запрошення' };
  }
};

// Отклонение приглашения (удаление из участников)
export const declineInvitation = async (
  invitationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  console.log('[declineInvitation] ===== START =====');
  console.log('[declineInvitation] Input params:', { invitationId, userId });
  
  if (!isFirebaseAvailable()) {
    console.log('[declineInvitation] Firebase not available');
    return { success: false, error: 'Firebase not available' };
  }

  const db = getDbInstance();
  if (!db) {
    console.log('[declineInvitation] DB not available');
    return { success: false, error: 'Firebase not available' };
  }

  try {
    // Пробуем найти приглашение по переданному ID
    console.log('[declineInvitation] Trying to fetch invitation by ID:', invitationId);
    let invitationDoc = await getDoc(doc(db, 'invitations', invitationId));
    
    // Если не найдено по ID, пробуем найти по шаблону {appId}_{userId}
    if (!invitationDoc.exists()) {
      console.log('[declineInvitation] Invitation not found by direct ID, trying appId_userId pattern');
      const parts = invitationId.split('_');
      if (parts.length >= 2) {
        const alternateId = `${invitationId}_${userId}`;
        console.log('[declineInvitation] Trying alternate ID:', alternateId);
        invitationDoc = await getDoc(doc(db, 'invitations', alternateId));
      }
    }
    
    // Если всё ещё не найдено, пробуем искать среди всех приглашений для пользователя
    if (!invitationDoc.exists()) {
      console.log('[declineInvitation] Searching for invitation by userId in all pending invitations');
      const allInvitations = await getDocs(query(
        collection(db, 'invitations'),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      ));
      
      for (const docSnap of allInvitations.docs) {
        const data = docSnap.data();
        if (data.appId === invitationId || docSnap.id.includes(invitationId)) {
          console.log('[declineInvitation] Found invitation by search:', docSnap.id);
          invitationDoc = docSnap;
          break;
        }
      }
    }
    
    if (!invitationDoc.exists()) {
      console.log('[declineInvitation] Invitation not found with any method');
      return { success: false, error: 'Запрошення не знайдено' };
    }
    
    const invitationData = invitationDoc.data();
    const realInvitationId = invitationDoc.id;
    console.log('[declineInvitation] Invitation found, real ID:', realInvitationId, 'data:', { status: invitationData.status, appId: invitationData.appId });
    
    if (invitationData.status !== 'pending') {
      console.log('[declineInvitation] Invitation not pending, status:', invitationData.status);
      return { success: false, error: 'Запрошення не знайдено або вже оброблено' };
    }

    const appId = invitationData.appId;
    
    // Получаем проект и удаляем из members
    console.log('[declineInvitation] Fetching app:', appId);
    const appDoc = await getDoc(doc(db, 'apps', appId));
    if (appDoc.exists()) {
      const appData = appDoc.data();
      const members = appData.members || {};
      
      console.log('[declineInvitation] Current members:', Object.keys(members));
      
      // FIX: Instead of deleting the member, update status to 'declined'
      // This works with Firestore rules which allow pending users to change their status
      const memberPath = `members.${userId}`;
      console.log('[declineInvitation] Using dot notation to update status to declined:', memberPath);
      
      await updateDoc(doc(db, 'apps', appId), {
        [memberPath]: {
          role: invitationData.role,
          status: 'declined',
          declinedAt: new Date().toISOString(),
          email: invitationData.email,
          name: invitationData.email?.split('@')[0] || 'User',
          invitedBy: invitationData.invitedBy
        },
        updatedAt: serverTimestamp()
      });
      console.log('[declineInvitation] updateDoc for apps/ succeeded (deleted member)');
    }

    // Обновляем статус приглашения на 'declined' (НЕ удаляем)
    console.log('[declineInvitation] Updating invitation status to declined, ID:', realInvitationId);
    await updateDoc(doc(db, 'invitations', realInvitationId), {
      status: 'declined',
      declinedAt: new Date().toISOString()
    });
    console.log('[declineInvitation] updateDoc for invitations succeeded');
    console.log('[declineInvitation] ===== SUCCESS =====');

    return { success: true };
  } catch (error: any) {
    console.error('[declineInvitation] Error:', error?.code, error?.message);
    return { success: false, error: 'Помилка при відхиленні запрошення' };
  }
};

// Удаление участника из всех проектов (вызывается при удалении участника из любого проекта или при удалении аккаунта)
export const removeMemberFromAllProjects = async (
  memberId: string,
  currentUser?: User
): Promise<{ success: boolean; error?: string; projectsCount?: number }> => {
  if (!isFirebaseAvailable()) {
    return { success: false, error: 'Firebase not available' };
  }

  const db = getDbInstance();
  if (!db) {
    return { success: false, error: 'Firebase not available' };
  }

  try {
    // Находим все проекты где пользователь является участником
    const allAppsSnapshot = await getDocs(query(collection(db, 'apps'), orderBy('updatedAt', 'desc')));
    const projectsToUpdate: string[] = [];
    
    allAppsSnapshot.forEach((appDoc) => {
      const appData = appDoc.data();
      const members = appData.members || {};
      
      // Проверяем, является ли пользователь участником (не владельцем)
      if (members[memberId] && appData.ownerId !== memberId) {
        projectsToUpdate.push(appDoc.id);
      }
    });
    
    console.log('[removeMemberFromAllProjects] Found projects with member:', projectsToUpdate.length);
    
    // Удаляем пользователя из всех проектов
    for (const appId of projectsToUpdate) {
      try {
        const appDoc = await getDoc(doc(db, 'apps', appId));
        if (appDoc.exists()) {
          const appData = appDoc.data();
          const members = { ...appData.members };
          delete members[memberId];
          
          await updateDoc(doc(db, 'apps', appId), {
            members,
            updatedAt: serverTimestamp()
          });
          
          // Удаляем приглашение если есть
          try {
            await deleteDoc(doc(db, 'invitations', `${appId}_${memberId}`));
          } catch (e) {
            // Ignore if invitation doesn't exist
          }
          
          console.log('[removeMemberFromAllProjects] Removed member from project:', appId);
        }
      } catch (error) {
        console.error('[removeMemberFromAllProjects] Error updating project:', appId, error);
      }
    }
    
    return { success: true, projectsCount: projectsToUpdate.length };
  } catch (error) {
    console.error('[removeMemberFromAllProjects] Error:', error);
    return { success: false, error: 'Помилка при видаленні учасника з проектів' };
  }
};

// Обновление email пользователя во всех проектах
export const syncUserEmailInAllProjects = async (
  memberId: string,
  newEmail: string,
  newName: string
): Promise<{ success: boolean; error?: string; projectsCount?: number }> => {
  if (!isFirebaseAvailable()) {
    return { success: false, error: 'Firebase not available' };
  }

  const db = getDbInstance();
  if (!db) {
    return { success: false, error: 'Firebase not available' };
  }

  try {
    // Находим все проекты где пользователь является участником
    const allAppsSnapshot = await getDocs(query(collection(db, 'apps'), orderBy('updatedAt', 'desc')));
    const projectsToUpdate: string[] = [];
    
    allAppsSnapshot.forEach((appDoc) => {
      const appData = appDoc.data();
      const members = appData.members || {};
      
      // Проверяем, является ли пользователь участником
      if (members[memberId]) {
        projectsToUpdate.push(appDoc.id);
      }
    });
    
    console.log('[syncUserEmailInAllProjects] Found projects with member:', projectsToUpdate.length);
    
    // Обновляем email и name во всех проектах
    for (const appId of projectsToUpdate) {
      try {
        const memberPath = `members.${memberId}`;
        await updateDoc(doc(db, 'apps', appId), {
          [memberPath]: {
            email: newEmail,
            name: newName
          },
          updatedAt: serverTimestamp()
        });
        
        console.log('[syncUserEmailInAllProjects] Updated member in project:', appId);
      } catch (error) {
        console.error('[syncUserEmailInAllProjects] Error updating project:', appId, error);
      }
    }
    
    return { success: true, projectsCount: projectsToUpdate.length };
  } catch (error) {
    console.error('[syncUserEmailInAllProjects] Error:', error);
    return { success: false, error: 'Помилка при синхронізації email' };
  }
};
// Удаление участника через Cloud Function
export const removeMember = async (
  appId: string,
  memberId: string,
  currentUser: User
): Promise<{ data: { success: boolean; error?: string }; isPending: boolean }> => {
  console.log('[removeMember] Starting with user:', { id: currentUser.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[removeMember] Functions not available');
    return { data: { success: false, error: 'Firebase Functions not available' }, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).removeMemberSchema as any,
    {
      appId,
      memberId,
    }
  );

  if (!validation.success) {
    console.error('[removeMember] Validation error:', validation.error);
    return { data: { success: false, error: validation.error || 'Помилка валідації' }, isPending: false };
  }

  try {
    const removeMemberFn = httpsCallable(functions, 'removeMember');
    const result = await removeMemberFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[removeMember] Member removed via Cloud Function:', memberId);
      
      await logHistory('update', 'project', appId, appId, null, { removedMember: memberId }, currentUser);
      
      return { data: { success: true }, isPending: false };
    }
    
    console.error('[removeMember] Cloud Function returned error:', result);
    return { data: { success: false, error: 'Failed to remove member' }, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[removeMember] Error:', errorMessage);
    return { data: { success: false, error: errorMessage }, isPending: false };
  }
};

// Обновление роли участника через Cloud Function
export const updateMemberRole = async (
  appId: string,
  memberId: string,
  newRole: string,
  currentUser: User
): Promise<{ data: { success: boolean; error?: string }; isPending: boolean }> => {
  console.log('[updateMemberRole] Starting with user:', { id: currentUser.id });

  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[updateMemberRole] Functions not available');
    return { data: { success: false, error: 'Firebase Functions not available' }, isPending: false };
  }

  // Валидация на фронтенде
  const validation = validate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (await import('../validators')).updateMemberRoleSchema as any,
    {
      appId,
      memberId,
      role: newRole,
    }
  );

  if (!validation.success) {
    console.error('[updateMemberRole] Validation error:', validation.error);
    return { data: { success: false, error: validation.error || 'Помилка валідації' }, isPending: false };
  }

  try {
    const updateMemberRoleFn = httpsCallable(functions, 'updateMemberRole');
    const result = await updateMemberRoleFn(validation.data);
    
    const response = result.data as { success: boolean };
    
    if (response.success) {
      console.log('[updateMemberRole] Member role updated via Cloud Function:', memberId, newRole);
      
      await logHistory('update', 'project', appId, appId, null, { updatedMemberRole: memberId, newRole }, currentUser);
      
      return { data: { success: true }, isPending: false };
    }
    
    console.error('[updateMemberRole] Cloud Function returned error:', result);
    return { data: { success: false, error: 'Failed to update member role' }, isPending: false };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[updateMemberRole] Error:', errorMessage);
    return { data: { success: false, error: errorMessage }, isPending: false };
  }
};

// Импорт проекта с полными данными через Cloud Function
export const importProject = async (
  data: { name: string; description?: string; versions?: AppVersion[]; isDemo?: boolean },
  user: User
): Promise<{ appId: string | null; success: boolean; error?: string }> => {
  console.log('[importProject] Starting with user:', { id: user.id }, 'data:', { name: data.name, versionsCount: data.versions?.length });
  
  const functions = getFunctionsInstance();
  if (!functions) {
    console.log('[importProject] Functions not available');
    return { appId: null, success: false, error: 'Firebase Functions not available' };
  }

  try {
    const importProjectFn = httpsCallable(functions, 'importProject');
    const result = await importProjectFn({
      name: data.name,
      description: data.description || '',
      versions: data.versions || [],
      isDemo: data.isDemo || false
    });
    
    const response = result.data as { appId: string; success: boolean };
    
    if (response.success && response.appId) {
      console.log('[importProject] Project imported via Cloud Function:', response.appId);
      
      // Получаем созданный проект из Firestore
      const createdApp = await getProject(response.appId);
      
      if (createdApp) {
        await logHistory('create', 'project', response.appId, response.appId, null, { name: data.name, description: data.description }, user);
        return { appId: response.appId, success: true };
      }
      
      return { appId: response.appId, success: true };
    }
    
    console.error('[importProject] Cloud Function returned error:', result);
    return { appId: null, success: false, error: 'Failed to import project' };
  } catch (error: any) {
    const errorMessage = handleFirebaseError(error);
    console.error('[importProject] Error:', errorMessage);
    return { appId: null, success: false, error: errorMessage };
  }
};

// Обновление своей роли участником (не владельцем)
export const updateMyRole = async (
  appId: string,
  newRole: string,
  currentUser: User
): Promise<{ success: boolean; error?: string }> => {
  if (!isFirebaseAvailable()) {
    return { success: false, error: 'Firebase not available' };
  }

  const db = getDbInstance();
  if (!db) {
    return { success: false, error: 'Firebase not available' };
  }

  try {
    const appDoc = await getDoc(doc(db, 'apps', appId));
    if (!appDoc.exists()) {
      return { success: false, error: 'Додаток не знайдено' };
    }

    const appData = appDoc.data();
    const members = appData.members || {};
    const ownerId = appData.ownerId;

    // Нельзя менять роль если ты владелец
    if (ownerId === currentUser.id) {
      return { success: false, error: 'Власник не може змінити свою роль' };
    }

    // Проверяем, что пользователь действительно участник
    if (!members[currentUser.id]) {
      return { success: false, error: 'Ви не є учасником цього проекту' };
    }

    // Обновить свою роль
    const updatedMembers = {
      ...members,
      [currentUser.id]: {
        ...members[currentUser.id],
        role: newRole
      }
    };

    await updateDoc(doc(db, 'apps', appId), {
      members: updatedMembers,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating my role:', error);
    return { success: false, error: 'Помилка при оновленні ролі' };
  }
};
